import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Videocam as VideocamIcon,
  Stop as StopIcon,
  Schedule as ScheduleIcon,
  RadioButtonChecked as LiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../contexts/AuthContext";
import liveStreamService, { LiveStream, StreamCredentials, LiveStreamService } from '../services/live-stream-service';
import LiveStreamBroadcaster from '../components/LiveStream/LiveStreamBroadcaster';

const BroadcastPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<'setup' | 'broadcasting' | 'ended'>('setup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  
  // Stream setup
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  
  // Stream data
  const [currentStream, setCurrentStream] = useState<LiveStream | null>(null);
  const [streamCredentials, setStreamCredentials] = useState<StreamCredentials | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      setCountdown(null);
    };
  }, []);

  // Hide body scroll and layout when broadcasting
  useEffect(() => {
    if (step === 'broadcasting') {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('broadcasting-mode');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('broadcasting-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('broadcasting-mode');
    };
  }, [step]);

  const handleScheduleStream = async () => {
    if (!title.trim()) {
      setError('Please enter a stream title');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // If no scheduled time is set, this is an instant stream - don't schedule, just prepare
      if (!scheduledAt) {
        // For instant streams, we'll create the stream when "Go Live!" is clicked
        // Just set up the UI to show ready state
        setCurrentStream({
          streamId: '', // Will be set when stream actually starts
          userId: user?.id || '',
          title: title.trim(),
          description: description.trim() || undefined,
          status: 'scheduled' as any, // Temporary status for UI
          ivsChannelArn: '',
          ivsStreamKey: '',
          ivsPlaybackUrl: '',
          ivsChatRoomArn: '',
          maxViewers: 0,
          totalViewers: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        setSuccess('Ready to go live! Click "Go Live!" to start your instant stream.');
      } else {
        // For scheduled streams, use the schedule API
        const stream = await liveStreamService.scheduleStream({
          title: title.trim(),
          description: description.trim() || undefined,
          scheduledAt: scheduledAt,
        });
        
        setCurrentStream(stream);
        setSuccess('Stream scheduled successfully!');
      }
    } catch (err) {
      setError(LiveStreamService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = (seconds: number) => {
    setCountdown(seconds);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setSuccess('Stream is ready to go live!');
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStartBroadcast = async () => {
    if (!currentStream) {
      setError('No stream prepared');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (currentStream.streamId && scheduledAt) {
        // This is a scheduled stream - start it with streamId
        const credentials = await liveStreamService.startStream(currentStream.streamId);
        setStreamCredentials(credentials);
        setStep('broadcasting');
        setSuccess('Stream started! You are now live.');
      } else {
        // This is an instant stream - create and start it
        const result = await liveStreamService.createInstantStream(currentStream.title, currentStream.description);
        
        // Update current stream with actual stream data
        setCurrentStream(result.stream);
        setStreamCredentials(result.credentials);
        setStep('broadcasting');
        setSuccess('Stream started! You are now live.');
      }
    } catch (err) {
      setError(LiveStreamService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEndBroadcast = async () => {
    if (!currentStream) return;

    try {
      setLoading(true);
      setError(null);
      setStep('ended');
      setSuccess('Stream ended successfully!');
    } catch (err) {
      setError(LiveStreamService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStreams = () => {
    navigate('/live');
  };

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          📺 Start Broadcasting
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Share your fashion style with the world through live streaming
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Setup Step */}
      {step === 'setup' && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <ScheduleIcon sx={{ mr: 1 }} />
              Stream Setup
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Stream Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Summer Fashion Haul 2025"
                  required
                  helperText="Give your stream an engaging title"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description (Optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell viewers what to expect from your stream..."
                  helperText="Describe what you'll be showcasing"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Schedule For (Optional)"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  helperText="Leave empty to start in 20 seconds"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleBackToStreams}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleScheduleStream}
                    disabled={loading || !title.trim()}
                    startIcon={loading ? <CircularProgress size={20} /> : <ScheduleIcon />}
                    sx={{
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                      },
                    }}
                  >
                    {loading ? 'Setting up...' : 'Setup Stream'}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {currentStream && (
              <Box sx={{ mt: 4, p: 3, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'success.dark' }}>
                  ✅ Stream Ready!
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Stream ID:</strong> {currentStream.streamId || 'Will be generated when you go live'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Status:</strong> <Chip label={currentStream.status} size="small" color="primary" />
                </Typography>
                
                {countdown !== null && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                      {countdown}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      seconds until stream starts
                    </Typography>
                  </Box>
                )}
                
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleStartBroadcast}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <LiveIcon />}
                  size="large"
                  sx={{
                    background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FF5252, #26C6DA)',
                    },
                  }}
                >
                  {loading ? 'Starting...' : 'Go Live!'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Broadcasting Step - Full Screen */}
      {step === 'broadcasting' && currentStream && streamCredentials && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999, // Above everything including header, footer, modals
            backgroundColor: '#000',
            overflow: 'hidden',
          }}
        >
          <LiveStreamBroadcaster
            streamId={currentStream.streamId}
            title={currentStream.title}
            initialCredentials={streamCredentials || undefined}
            onStreamEnd={handleEndBroadcast}
            onError={setError}
            showAdRevenue={true}
            enableModeration={true}
          />
        </Box>
      )}

      {/* Ended Step */}
      {step === 'ended' && (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              🎉 Stream Ended Successfully!
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4 }}>
              Thank you for sharing your fashion content with the community.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep('setup');
                  setCurrentStream(null);
                  setStreamCredentials(null);
                  setTitle('');
                  setDescription('');
                  setScheduledAt('');
                  setCountdown(null);
                  setError(null);
                  setSuccess(null);
                }}
              >
                Start New Stream
              </Button>
              <Button
                variant="contained"
                onClick={handleBackToStreams}
              >
                Back to Streams
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default BroadcastPage;